/**
 * analytics.js — Medición de uso del producto (GA4 vía Google Tag Manager).
 *
 * POR QUÉ ASÍ
 * El index.html ya carga GTM y configura Consent Mode v2 con todo denegado
 * por defecto; el banner de cookies lo pasa a `granted`. Entonces el camino
 * correcto es empujar al `dataLayer` y dejar que GTM decida: si el visitante
 * no aceptó, Google no envía nada. Por eso acá NO se chequea el consentimiento
 * a mano — hacerlo duplicaría la lógica y se desincronizaría con el banner.
 *
 * `vue-gtag` figura en package.json pero nunca se usó: cargar GA por su cuenta
 * duplicaría los hits que ya manda GTM.
 *
 * QUÉ NO SE MANDA
 * Nada que identifique a una persona: ni nombres de clientes, ni emails, ni
 * teléfonos, ni CUIT, ni direcciones. GA4 prohíbe los datos personales y
 * además no hacen falta para responder lo que se quiere saber ("¿cuántos
 * presupuestos se hicieron esta semana?"). `sanitizarParams` actúa de red de
 * seguridad por si alguna vez se cuela una clave sospechosa en un call site.
 *
 * CÓMO AGREGAR UN EVENTO
 * 1. Sumarlo a EVENTOS con un nombre snake_case en inglés (ver nota abajo).
 * 2. Llamarlo desde el store, no desde el componente: el store es el único
 *    lugar por el que pasan todos los que ejecutan esa acción.
 * 3. Sólo después de que la operación haya salido bien.
 *
 * Los nombres van en inglés snake_case porque conviven con los eventos
 * reservados de GA4 (`login`, `search`, `page_view`), que son en inglés y no
 * se pueden renombrar. La referencia en castellano está en docs/analytics.md.
 */

const MAX_LARGO_VALOR = 100;
const MAX_PARAMS = 25;

// Claves que nunca pueden viajar a Google, por si se cuelan en un call site.
const CLAVES_PROHIBIDAS = /(^|_)(email|mail|nombre|apellido|telefono|phone|cuit|dni|direccion|address|password|token|razon_social)($|_)/i;

const esDesarrollo = process.env.NODE_ENV === 'development';

/**
 * Catálogo de eventos. Tenerlos acá y no como strings sueltos evita el
 * problema clásico de GA: `venta_creada`, `ventaCreada` y `sale_created`
 * conviviendo como tres métricas distintas de lo mismo.
 */
export const EVENTOS = {
  // Navegación y sesión.
  //
  // Se llama `virtual_page_view` y no `page_view` a propósito: GTM suele traer
  // un activador "History Change" para las SPA, y si acá empujáramos un evento
  // llamado `page_view` correríamos el riesgo de que GA4 cuente dos veces la
  // misma pantalla. Con un nombre propio, el contenedor decide: si ya mide la
  // navegación, ignora este evento; si no, lo mapea a `page_view`.
  PAGE_VIEW: 'virtual_page_view',
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGN_UP: 'sign_up',
  ONBOARDING_COMPLETADO: 'onboarding_complete',
  NAV_CLICK: 'nav_click',
  PWA_INSTALADA: 'pwa_install',

  // Ventas
  VENTA_CREADA: 'sale_created',
  VENTA_ACTUALIZADA: 'sale_updated',
  VENTA_ANULADA: 'sale_voided',
  VENTA_ESTADO_CAMBIADO: 'sale_status_changed',
  VENTA_PAGO_REGISTRADO: 'sale_payment_added',

  // Presupuestos.
  //
  // No hay `quote_converted_to_sale`: convertir es navegar a /venta con el
  // presupuesto cargado, así que la conversión se mide con `from_quote` dentro
  // de `sale_created`. Un evento aparte contaría también las conversiones que
  // el usuario abandona sin guardar la venta.
  PRESUPUESTO_CREADO: 'quote_created',
  PRESUPUESTO_ACTUALIZADO: 'quote_updated',
  PRESUPUESTO_ELIMINADO: 'quote_deleted',
  PRESUPUESTO_COMPARTIDO: 'quote_shared',

  // Productos
  PRODUCTO_CREADO: 'product_created',
  PRODUCTO_ACTUALIZADO: 'product_updated',
  PRODUCTO_ELIMINADO: 'product_deleted',
  PRODUCTO_DUPLICADO: 'product_duplicated',
  PRODUCTOS_IMPORTADOS: 'products_imported',
  STOCK_AJUSTADO: 'stock_adjusted',

  // Gastos, clientes, proveedores, compras
  GASTO_CREADO: 'expense_created',
  GASTO_ELIMINADO: 'expense_deleted',
  CLIENTE_CREADO: 'client_created',
  CLIENTE_ACTUALIZADO: 'client_updated',
  PROVEEDOR_CREADO: 'supplier_created',
  PEDIDO_CREADO: 'purchase_order_created',
  PEDIDO_ESTADO_CAMBIADO: 'purchase_order_status_changed',

  // Catálogo público
  CATALOGO_PUBLICADO: 'catalog_published',
  CATALOGO_VISIBILIDAD: 'catalog_visibility_changed',
  CATALOGO_PDF: 'catalog_pdf_exported',

  // Herramientas
  BUSQUEDA: 'search',
  ESCANEO_CODIGO: 'barcode_scanned',
  ASISTENTE_MENSAJE: 'ai_message_sent',
  // Ver un reporte ya queda cubierto por `virtual_page_view` de /reportes:
  // un evento propio sería la misma métrica contada dos veces.
  DOCUMENTO_PDF: 'document_pdf_generated',
  CONFIG_GUARDADA: 'settings_saved',
};

function esNombreValido(nombre) {
  // Restricciones de GA4: empieza con letra, sólo [A-Za-z0-9_], hasta 40.
  return typeof nombre === 'string' && /^[a-z][a-z0-9_]{0,39}$/.test(nombre);
}

/**
 * Deja los parámetros dentro de lo que GA4 acepta y saca cualquier cosa que
 * parezca un dato personal.
 */
function sanitizarParams(params = {}) {
  const limpio = {};
  let cantidad = 0;

  for (const [clave, valor] of Object.entries(params)) {
    if (cantidad >= MAX_PARAMS) break;
    if (valor === null || valor === undefined || valor === '') continue;

    if (CLAVES_PROHIBIDAS.test(clave)) {
      if (esDesarrollo) {
        console.warn(`[analytics] se descartó "${clave}": parece un dato personal`);
      }
      continue;
    }

    if (typeof valor === 'number') {
      if (!Number.isFinite(valor)) continue;
      // Dos decimales alcanzan para importes y evita ruido de coma flotante.
      limpio[clave] = Math.round(valor * 100) / 100;
    } else if (typeof valor === 'boolean') {
      limpio[clave] = valor;
    } else {
      limpio[clave] = String(valor).slice(0, MAX_LARGO_VALOR);
    }

    cantidad++;
  }

  return limpio;
}

function push(objeto) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(objeto);
}

/**
 * Registra un evento. Nunca tira: una métrica rota no puede voltear una venta.
 */
export function trackEvent(nombre, params = {}) {
  try {
    if (!esNombreValido(nombre)) {
      if (esDesarrollo) console.warn(`[analytics] nombre de evento inválido: "${nombre}"`);
      return;
    }

    const datos = sanitizarParams(params);
    push({ event: nombre, ...datos });

    if (esDesarrollo) console.debug('[analytics]', nombre, datos);
  } catch (error) {
    if (esDesarrollo) console.warn('[analytics] no se pudo registrar el evento:', error);
  }
}

/**
 * Vista de página. La app es una SPA: sin esto GA sólo vería la primera carga.
 */
export function trackPageView(ruta) {
  trackEvent(EVENTOS.PAGE_VIEW, {
    page_path: ruta?.path,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
    // El nombre de la ruta es más estable que el path para agrupar en reportes:
    // /clientes/42 y /clientes/87 son la misma pantalla.
    screen_name: ruta?.name,
  });
}

/**
 * Propiedades de usuario: sirven para segmentar (ej. "presupuestos creados por
 * plan"). Sólo datos de la cuenta, nunca de la persona.
 */
export function setUserProperties({ plan, rol, empresaId } = {}) {
  try {
    push({
      event: 'set_user_properties',
      user_properties: sanitizarParams({
        plan,
        rol,
        // Pseudónimo: permite contar cuentas distintas sin identificar a nadie.
        empresa_id: empresaId,
      }),
    });
  } catch (error) {
    if (esDesarrollo) console.warn('[analytics] no se pudieron setear propiedades:', error);
  }
}

/**
 * Helper para los documentos comerciales, que comparten forma: importe,
 * cantidad de ítems y si tienen cliente asociado.
 *
 * OJO: el importe va como `value` pero el evento NO es `purchase`. Una venta
 * acá es facturación DEL CLIENTE de Olitom, no de Olitom: mandarla como
 * `purchase` metería la facturación ajena en los reportes de ingresos.
 */
export function paramsDocumento({ total, items, clienteId, moneda = 'ARS' } = {}) {
  const lineas = Array.isArray(items) ? items : [];

  // El importe se calcula desde los ítems porque los payloads de venta y
  // presupuesto no llevan `total`: lo arma el backend. Sumarlo acá evita tener
  // que esperar la respuesta y que el evento dependa de su forma.
  const importe = Number.isFinite(Number(total)) && Number(total) > 0
    ? Number(total)
    : lineas.reduce(
      (acc, i) => acc + (Number(i?.precio ?? i?.precio_unitario) || 0) * (Number(i?.cantidad) || 0),
      0,
    );

  return {
    value: Math.round(importe * 100) / 100,
    currency: moneda,
    items_count: lineas.length || Number(items) || 0,
    has_client: Boolean(clienteId),
  };
}

export default { trackEvent, trackPageView, setUserProperties, paramsDocumento, EVENTOS };
